import { equals } from '@execonline-inc/collections';
import { when } from '@execonline-inc/maybe-adapter';
import { InvalidUrlError } from '@execonline-inc/url';
import { always } from '@kofno/piper';
import { BadStatus } from 'ajaxian';
import { Maybe, just, nothing } from 'maybeasy';
import { AppyError } from '../../../../../Appy';
import { MissingLinkError } from '../../../../../LinkyLinky';
import ParticipantsStore from '../../../../../ParticipantsStore';
import {
  FormField,
  FormOutValues,
  FormTemplate,
  HasFormTemplates,
  MissingFieldError,
  MultipleSelectionsField,
  OptionImage,
  SelectionOption,
} from '../../../../../Resource/FormTemplates/Types';
import { Link, Resource } from '../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../Translations';
import { LicenseResource } from '../../Table/LicenseAllocations/Types';

interface Waiting {
  kind: 'waiting';
}

export interface NewBulkLicenseAllocationsPayload {}

export type NewBulkLicenseAllocationsResource = Resource<NewBulkLicenseAllocationsPayload> &
  HasFormTemplates;

export interface Loading {
  kind: 'loading';
  link: Link;
  participantsStore: ParticipantsStore;
}

export interface MissingTemplateError {
  kind: 'missing-template-error';
  template: string;
}

export const missingTemplate = (template: string): MissingTemplateError => ({
  kind: 'missing-template-error',
  template,
});

export interface InvalidFormValues {
  kind: 'invalid-form-values';
  errors: ReadonlyArray<string>;
  fields: ReadonlyArray<FormField>;
}

export const invalidFormValues = (
  errors: ReadonlyArray<string>,
  fields: ReadonlyArray<FormField>,
): InvalidFormValues => ({
  kind: 'invalid-form-values',
  errors,
  fields,
});

const whenOneInvalidField = (error: InvalidFormValues): Maybe<FormField> =>
  just(error.fields.length)
    .andThen(when(equals(1)))
    .map(always(error.fields[0]));

const whenMultipleSelectionsField = (field: FormField): Maybe<MultipleSelectionsField> =>
  field.kind === 'multiple-selection' ? just(field) : nothing();

const whenOneUserIdError = (field: MultipleSelectionsField): Maybe<MultipleSelectionsField> =>
  just(field.name)
    .andThen(when(equals('user_ids')))
    .map(always(field.errors.length))
    .andThen(when(equals(1)))
    .map(always(field));

const exceededMaxUsers = (field: MultipleSelectionsField): Maybe<number> =>
  whenOneUserIdError(field)
    .map(always(field.maxSelections))
    .andThen(when((maxSelections) => maxSelections < field.value.length));

const noUsersSelected = (field: MultipleSelectionsField): Maybe<MultipleSelectionsField> =>
  whenOneUserIdError(field)
    .map(always(field.value.length))
    .andThen(when(equals(0)))
    .map(always(field));

export const whenExceededMaxUsers = (error: InvalidFormValues): Maybe<number> =>
  whenOneInvalidField(error).andThen(whenMultipleSelectionsField).andThen(exceededMaxUsers);

export const whenNoUsersSelected = (error: InvalidFormValues): Maybe<InvalidFormValues> =>
  whenOneInvalidField(error)
    .andThen(whenMultipleSelectionsField)
    .andThen(noUsersSelected)
    .map(always(error));

export type LoadError =
  | AppyError
  | MissingTemplateError
  | MissingLinkError
  | MissingFieldError
  | InvalidFormValues
  | InvalidUrlError;

export interface LoadingError {
  kind: 'loading-error';
  error: LoadError;
}

export interface BulkParticipantsLicenseFormPayload {
  allocatableOrReleasableLicenses: ReadonlyArray<LicenseResource>;
}

export type BulkParticipantsLicenseFormResource = Resource<BulkParticipantsLicenseFormPayload> &
  HasFormTemplates;

export interface InitialReady {
  kind: 'initial-ready';
  resource: NewBulkLicenseAllocationsResource;
  template: FormTemplate;
  participantsStore: ParticipantsStore;
}

export interface LicensesReady {
  kind: 'licenses-ready';
  resource: BulkParticipantsLicenseFormResource;
  template: FormTemplate;
  participantsStore: ParticipantsStore;
  selectedLicenses: SelectableLicense[];
  initialResource: NewBulkLicenseAllocationsResource;
  initialTemplate: FormTemplate;
}

export interface Submitting {
  kind: 'submitting';
  resource: BulkParticipantsLicenseFormResource;
  template: FormTemplate;
  values: FormOutValues;
  participantsStore: ParticipantsStore;
}

export interface FetchingLicenseOptions {
  kind: 'fetching-license-options';
  resource: NewBulkLicenseAllocationsResource;
  template: FormTemplate;
  participantsStore: ParticipantsStore;
}

export type FetchingLicenseOptionsErrorType =
  | AppyError
  | MissingLinkError
  | InvalidUrlError
  | MissingTemplateError
  | MissingFieldError
  | InvalidFormValues;

export interface FetchingLicenseOptionsError {
  kind: 'fetching-license-options-error';
  resource: NewBulkLicenseAllocationsResource;
  template: FormTemplate;
  error: FetchingLicenseOptionsErrorType;
  participantsStore: ParticipantsStore;
}

export type SubmitError = AppyError | MissingLinkError | InvalidUrlError;

export interface SubmittingError {
  kind: 'submitting-error';
  resource: BulkParticipantsLicenseFormResource;
  template: FormTemplate;
  error: SubmitError;
}

export type ParticipantLicenseAction = 'allocate' | 'release';

export interface ParticipantBulkLicenseSubmittedPayload {
  allocatedOrReleasedLicenses: ReadonlyArray<LicenseResource>;
  userIds: ReadonlyArray<number>;
  licenseAction: ParticipantLicenseAction;
}

export type ParticipantBulkLicenseSubmittedResource =
  Resource<ParticipantBulkLicenseSubmittedPayload>;

export interface Submitted {
  kind: 'submitted';
  resource: ParticipantBulkLicenseSubmittedResource;
  template: FormTemplate;
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (link: Link, participantsStore: ParticipantsStore): Loading => ({
  kind: 'loading',
  link,
  participantsStore,
});

export const loadingError = (error: LoadError): LoadingError => ({
  kind: 'loading-error',
  error,
});

export const initialReady = (
  resource: NewBulkLicenseAllocationsResource,
  template: FormTemplate,
  participantsStore: ParticipantsStore,
): InitialReady => ({
  kind: 'initial-ready',
  resource,
  template,
  participantsStore,
});

export const licensesReady = (
  resource: BulkParticipantsLicenseFormResource,
  template: FormTemplate,
  participantsStore: ParticipantsStore,
  selectedLicenses: SelectableLicense[] = [],
  initialResource: NewBulkLicenseAllocationsResource,
  initialTemplate: FormTemplate,
): LicensesReady => ({
  kind: 'licenses-ready',
  resource,
  template,
  participantsStore,
  selectedLicenses,
  initialResource,
  initialTemplate,
});

export const fetchingLicenseOptions = (
  resource: NewBulkLicenseAllocationsResource,
  template: FormTemplate,
  participantsStore: ParticipantsStore,
): FetchingLicenseOptions => ({
  kind: 'fetching-license-options',
  resource,
  template,
  participantsStore,
});

export const fetchingLicenseOptionsError = (
  resource: NewBulkLicenseAllocationsResource,
  template: FormTemplate,
  error: FetchingLicenseOptionsErrorType,
  participantsStore: ParticipantsStore,
): FetchingLicenseOptionsError => ({
  kind: 'fetching-license-options-error',
  resource,
  template,
  error,
  participantsStore,
});

export const submitting = (
  resource: BulkParticipantsLicenseFormResource,
  template: FormTemplate,
  values: FormOutValues,
  participantsStore: ParticipantsStore,
): Submitting => ({
  kind: 'submitting',
  resource,
  template,
  values,
  participantsStore,
});

export const submittingError = (
  resource: BulkParticipantsLicenseFormResource,
  template: FormTemplate,
  error: SubmitError,
): SubmittingError => ({
  kind: 'submitting-error',
  resource,
  template,
  error,
});

export const submitted = (
  resource: ParticipantBulkLicenseSubmittedResource,
  template: FormTemplate,
): Submitted => ({
  kind: 'submitted',
  resource,
  template,
});

/**
 * State Machine for Bulk License Allocation to Participants
 *
 * This state machine has additional states compared to the Groups implementation
 * because it supports dynamic license fetching based on the selected action:
 *
 * - InitialReady: Initial form state with action selection (allocate/release)
 * - FetchingLicenseOptions: Fetching available licenses based on selected action
 * - FetchingLicenseOptionsError: Error state when fetching licenses fails
 * - LicensesReady: Licenses loaded and ready for selection
 *
 * The Groups implementation is simpler because it doesn't need to dynamically
 * fetch different license sets based on user action selection.
 *
 * State Flow:
 * Waiting → Loading → InitialReady → FetchingLicenseOptions → LicensesReady → Submitting → Submitted
 *                  ↓                ↓                        ↓              ↓
 *              LoadingError   FetchingLicenseOptionsError  (can re-fetch)  SubmittingError
 */
export type State =
  | Waiting
  | Loading
  | LoadingError
  | InitialReady
  | FetchingLicenseOptions
  | FetchingLicenseOptionsError
  | LicensesReady
  | Submitting
  | SubmittingError
  | Submitted;

export interface SubmissionSuccess {
  kind: 'submission-success';
  resource: ParticipantBulkLicenseSubmittedResource;
}

export interface ValidationErrors {
  kind: 'validation-errors';
  resource: BulkParticipantsLicenseFormResource;
}

export type SubmissionResponse = SubmissionSuccess | ValidationErrors;

// License Selection Types
export type SelectableLicense =
  | { kind: 'unselected' }
  | { kind: 'selected'; licenseId: number; licenseName: string };

export const createUnselectedLicense = (): SelectableLicense => ({ kind: 'unselected' });

export const createSelectedLicense = (
  licenseId: number,
  licenseName: string,
): SelectableLicense => ({
  kind: 'selected',
  licenseId,
  licenseName,
});

export const licenseHasValue = (license: SelectableLicense): boolean => {
  return license.kind === 'selected';
};

// Helper function for syncing licenses to template
export const filterSelectedLicenses = (
  licenses: SelectableLicense[],
): Array<{ kind: 'selected'; licenseId: number; licenseName: string }> => {
  return licenses.filter(
    (l): l is { kind: 'selected'; licenseId: number; licenseName: string } => l.kind === 'selected',
  );
};

export const convertLicensesToSelectionOptions = (
  selectedLicenses: SelectableLicense[],
  licenses: ReadonlyArray<LicenseResource>,
): SelectionOption[] => {
  const licenseMap = new Map(licenses.map((license) => [license.payload.id, license]));

  return selectedLicenses
    .filter(
      (l): l is { kind: 'selected'; licenseId: number; licenseName: string } =>
        l.kind === 'selected',
    )
    .map((license) => {
      const matchingLicense = licenseMap.get(license.licenseId);
      return matchingLicense
        ? ({
            label: `${matchingLicense.payload.name}`,
            value: license.licenseId.toString(),
            image: nothing<OptionImage>(),
            link: nothing<Link>(),
          } as SelectionOption)
        : null;
    })
    .filter((opt): opt is SelectionOption => opt !== null);
};

/**
 * Extract user-friendly error message from BadStatus error
 *
 * NOTE: This is a temporary solution that relies on string matching of error messages.
 * TODO: Backend should return structured error responses with error codes instead of
 * raw error strings. Recommended format:
 * {
 *   error_code: "PARTICIPANTS_DIFFERENT_ORGS",
 *   message: "All participants must belong to the same org...",
 *   details: {...}
 * }
 */
export const getBadStatusErrorMessage = (error: BadStatus): TPlainTextKey => {
  // Extract error message from various possible structures
  const errorBody = error.response.body;
  let errorMessage = '';

  if (typeof errorBody === 'string') {
    errorMessage = errorBody;
  } else if (errorBody && typeof errorBody === 'object') {
    // Try different possible error message locations
    errorMessage =
      (errorBody as any).error || (errorBody as any).message || (errorBody as any).exception || '';
  }

  // Check if the error message contains our specific error
  if (
    errorMessage.includes(
      'All participants must belong to the same org when bulk license provisioning',
    )
  ) {
    return 'All participants must belong to the same org when bulk license provisioning';
  }

  return 'We are unable to process your request at this time';
};
